import { Checkbox, Form, Input, InputNumber, Modal, Select, Button, Space, Divider } from 'antd';
import { PlusOutlined, MinusCircleOutlined } from '@ant-design/icons';
import { useChangeHandler } from '@/hooks';
// import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { screensCleanUp } from 'state/actions/screens';
import {
  fetchProgrammaticVendors,
  updateProgrammaticVendor,
} from 'state/actions/programmaticVendor';
import { regexDescription, regexInput } from 'utils/regex';
import { log } from 'utils';
import { Option } from 'antd/lib/mentions';
import _ from 'underscore';

export default function ModalScreenForm({
  screen,
  setScreen,
  visible,
  onCancel,
  onCreate,
  isEditing,
}) {
  const dispatch = useDispatch();
  const { siteId } = useParams();

  const [form] = Form.useForm();

  const {
    loading,
    screenGroupList,
    mediaCategoryList,
    screenSchedulingList,
    programmaticVendors,
  } = useSelector(
    (state) => ({
      loading: state.sites.loading,
      screenGroupList: state.screengroup.list,
      mediaCategoryList: state.mediaCategories.list,
      screenSchedulingList: state.screenScheduling.list,
      programmaticVendors: state.programmaticVendor.list,
    }),
    shallowEqual
  );

  const [errors, setErrors] = useState({
    name: '',
    description: '',
  });

  const [allowMultipleConfig, setAllowMultipleConfig] = useState(screen.allowMultipleConfig || false);

  useEffect(() => {
    return () => dispatch(screensCleanUp());
  }, [dispatch]);

  useEffect(() => {
    if (visible) {
      dispatch(fetchProgrammaticVendors({ siteId }));
    }
  }, [dispatch, visible, siteId]);

  // Credential edits live outside the antd form: they save to the vendor
  // record, not to the screen. Shape: { [vendorId]: { orgUuid, apiKey } }.
  const [vendorEdits, setVendorEdits] = useState({});

  useEffect(() => {
    const seed = {};
    programmaticVendors.forEach((vendor) => {
      seed[vendor._id] = {
        // Never prefill the secret: the API only ever returns a preview.
        orgUuid: vendor.vendorData?.orgUuid || '',
        apiKey: '',
      };
    });
    setVendorEdits(seed);
  }, [programmaticVendors]);

  const changeVendorEdit = (vendorId, field, value) =>
    setVendorEdits((prev) => ({
      ...prev,
      [vendorId]: { ...prev[vendorId], [field]: value },
    }));

  // One vendor record is shared by every screen on the site, so saving here
  // rewrites the credentials for all of them — not just this screen.
  const saveVendorCredentials = () => {
    programmaticVendors.forEach((vendor) => {
      const edit = vendorEdits[vendor._id];
      if (!edit) return;

      const orgUuid = (edit.orgUuid || '').trim();
      const apiKey = (edit.apiKey || '').trim();
      const orgUuidChanged = orgUuid !== (vendor.vendorData?.orgUuid || '');
      if (!orgUuidChanged && !apiKey) return;

      const payload = {
        id: vendor._id,
        name: vendor.name,
        vendor: vendor.vendor,
        siteId: vendor.siteId || siteId,
        templateId: vendor.templateId || null,
        vendorData: { ...(vendor.vendorData || {}), orgUuid },
      };
      // Omit apiKey to keep the stored key; send it only when a new one is typed.
      if (apiKey) {
        payload.apiKey = apiKey;
      }
      dispatch(updateProgrammaticVendor(payload));
    });
  };

  const onChangeHandler = useChangeHandler(setScreen);

  const onChangeResolution = (field, value) => {
    // Remove non-numeric characters (allowing only numbers)
    const cleanedValue = value ? String(value).replace(/\D/g, '') : '';
    setScreen((prev) => ({
      ...prev,
      [field]: cleanedValue ? Number(cleanedValue) : null,
    }));
  };

  // Keys are lowercase vendor names. Ids for vendors not shown in this form
  // (another site's vendor, a vendor deleted since) are preserved, and blank
  // inputs drop their key so the mapping is removed.
  const buildExternalIds = (formValues) => {
    const merged = { ...(screen.programmaticExternalIds || {}) };
    programmaticVendors.forEach((vendor) => {
      const key = (vendor.vendor || '').toLowerCase();
      if (!key) return;
      const value = (formValues?.[key] || '').trim();
      if (value) {
        merged[key] = value;
      } else {
        delete merged[key];
      }
    });
    return merged;
  };

  const onSave = (values) => {
    if (
      (!regexInput(values.name) && values.name) ||
      (!regexDescription(values.description) && values.description)
    ) {
      setErrors({
        ...errors,
        name:
          !regexInput(values.name) &&
          values.name &&
          'This field cannot contain a special characters',
        description:
          !regexDescription(values.description) &&
          values.description &&
          'This field cannot contain a special characters',
      });
    } else {
      setErrors({
        name: '',
        description: '',
      });
      onCreate({
        ...screen,
        id: screen._id,
        siteId,
        name: values.name,
        adsumScreenId: values.adsumScreenId,
        description: values.description,
        isLive: values.isLive,
        width: values.width,
        height: values.height,
        groupId: values.groupId,
        mediaCategoryId: values.mediaCategoryId,
        allowMultipleConfig: values.allowMultipleConfig || false,
        screenConfigs: values.allowMultipleConfig ? (values.screenConfigs || []) : [],
        schedulingIds: values.schedulingIds || [],
        programmaticExternalIds: buildExternalIds(
          values.programmaticExternalIds
        ),
      });
      saveVendorCredentials();
      form.resetFields();
    }
  };
  const onChangeSelect = (keyString, value) => {
    setScreen((prevState) => ({
      ...prevState,
      [keyString]: value,
    }));
  };

  return (
    <Modal
      visible={visible}
      centered
      title={isEditing ? 'Edit Screen' : 'Create New Screen'}
      okText="Submit"
      confirmLoading={loading}
      onCancel={() => {
        onCancel();
        form.resetFields();
      }}
      onOk={() => {
        form
          .validateFields()
          .then((values) => {
            onSave(values);
          })
          .catch((info) => {
            log('Validate Failed:', info);
          });
      }}
    >
      <Form
        layout="vertical"
        form={form}
        onChange={onChangeHandler}
        initialValues={{
          name: screen.name,
          description: screen.description,
          adsumScreenId: screen.adsumScreenId,
          height: screen.height,
          width: screen.width,
          groupId: screen.groupId,
          isLive: screen.isLive,
          mediaCategoryId: screen.mediaCategoryId,
          allowMultipleConfig: screen.allowMultipleConfig || false,
          screenConfigs: screen.screenConfigs || [],
          schedulingIds: (screen.schedulingIds || []).map(s => s._id || s),
          programmaticExternalIds: screen.programmaticExternalIds || {},
        }}
      >
        <Form.Item
          label="Name"
          name="name"
          rules={[{ required: true }]}
          validateStatus={errors.name ? 'error' : 'success'}
          help={errors.name ? errors.name : null}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Adsum/Editor Screen Id"
          name="adsumScreenId"
          rules={[]}
          validateStatus={errors.name ? 'error' : 'success'}
          help={errors.name ? errors.name : null}
        >
          <Input />
        </Form.Item>

        <Form.Item
          label="Screen Group"
          name="groupId"
          validateStatus={errors.groupId ? 'error' : 'success'}
          help={errors.groupId ? errors.groupId : null}
        >
          <Select
            placeholder="Select Access"
            value={screen.groupId}
            allowClear
            onChange={(value) => onChangeSelect('groupId', value)}
            // disabled={userData && userData.role === 'admin'}
          >
            {screenGroupList.map((group) => (
              <Option key={group._id} value={group._id}>
                {group.name}
              </Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item
          label="Media Categories"
          name="mediaCategoryId"
          validateStatus={errors.mediaCategoryId ? 'error' : 'success'}
          help={errors.mediaCategoryId ? errors.mediaCategoryId : null}
        >
          <Select
            placeholder="Select Media Categories"
            value={screen.mediaCategoryId}
            mode={"multiple"}
            onChange={(value) => onChangeSelect('mediaCategoryId', value)}
            // disabled={userData && userData.role === 'admin'}
          >
            {_.sortBy(mediaCategoryList, 'name').map((group) => (
              <Option key={group._id} value={group._id}>
                {group.name}
              </Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          label="Screen Width"
          name="width"
          rules={[
            { type: 'number', min: 10, message: 'Width must be at least 100' },
          ]}
          validateStatus={errors.width ? 'error' : 'success'}
          help={errors.width ? errors.width : null}
        >
          <InputNumber
            name="width"
            value={screen.width}
            onChange={(value) => onChangeResolution('width', value)}
          />
        </Form.Item>
        <Form.Item
          label="Screen Height"
          name="height"
          rules={[
            { type: 'number', min: 100, message: 'Width must be at least 100' },
          ]}
          validateStatus={errors.height ? 'error' : 'success'}
          help={errors.height ? errors.height : null}
        >
          <InputNumber
            name="height"
            value={screen.height}
            onChange={(value) => onChangeResolution('height', value)}
          />
        </Form.Item>
        {programmaticVendors.length > 0 && (
          <>
            {programmaticVendors.map((vendor) => (
              <React.Fragment key={vendor._id}>
                <Form.Item
                  label={vendor.name}
                  name={[
                    'programmaticExternalIds',
                    (vendor.vendor || '').toLowerCase(),
                  ]}
                  help={`The unit external_id ${vendor.name} assigned to this screen.`}
                >
                  <Input placeholder="CTYLNK-L1-ATR-01" />
                </Form.Item>

                <Form.Item
                  label={`${vendor.name} — Organization UUID`}
                  help="Shared by every screen using this vendor."
                >
                  <Input
                    placeholder="3f2a9c40-1d55-4b8e-9a71-6c0d2e7f8b13"
                    value={vendorEdits[vendor._id]?.orgUuid || ''}
                    onChange={(e) =>
                      changeVendorEdit(vendor._id, 'orgUuid', e.target.value)
                    }
                  />
                </Form.Item>

                <Form.Item
                  label={`${vendor.name} — API Key`}
                  help="Leave blank to keep the stored key. Typing a new key rotates it for every screen using this vendor."
                >
                  <Input.Password
                    placeholder={vendor.apiKeyPreview || 'Stored key hidden'}
                    value={vendorEdits[vendor._id]?.apiKey || ''}
                    onChange={(e) =>
                      changeVendorEdit(vendor._id, 'apiKey', e.target.value)
                    }
                  />
                </Form.Item>
              </React.Fragment>
            ))}
          </>
        )}
        <Form.Item
          label="Description"
          name="description"
          validateStatus={errors.description ? 'error' : 'success'}
          help={errors.description ? errors.description : null}
        >
          <Input.TextArea rows={4} placeholder="Description" />
        </Form.Item>
        <Form.Item name="isLive" valuePropName="checked">
          <Checkbox>Screen is Live on Site</Checkbox>
        </Form.Item>
        <Form.Item
          name="allowMultipleConfig"
          valuePropName="checked"
        >
          <Checkbox onChange={(e) => setAllowMultipleConfig(e.target.checked)}>
            Allow Multiple Screen Configurations
          </Checkbox>
        </Form.Item>

        {allowMultipleConfig && (
          <>
            <div style={{fontWeight: 'bold', margin: '12px 0'}}>Screen Configurations</div>
            <Form.Item
              label="Scheduling"
              name="schedulingIds"
            >
              <Select
                mode="multiple"
                placeholder="Select Scheduling"
                allowClear
              >
                {screenSchedulingList.map((scheduling) => (
                  <Option key={scheduling._id} value={scheduling._id}>
                    {scheduling.name}
                  </Option>
                ))}
              </Select>
            </Form.Item>
            <br />
            <Form.List name="screenConfigs">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space
                      key={key}
                      style={{
                        display: 'flex',
                        marginBottom: 8,
                        border: '1px solid #d9d9d9',
                        padding: '16px',
                        borderRadius: '4px',
                        position: 'relative',
                      }}
                      align="baseline"
                      direction="vertical"
                    >
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ width: '80px', flexShrink: 0 }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'index']}
                            label="Index"
                            rules={[{ required: true, message: 'Index is required' }]}
                            style={{ marginBottom: 8 }}
                          >
                            <InputNumber min={0} placeholder="0" style={{ width: '100%' }} />
                          </Form.Item>
                        </div>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'name']}
                            label="Config Name"
                            rules={[{ required: true, message: 'Name is required' }]}
                            style={{ marginBottom: 8 }}
                          >
                            <Input placeholder="Config name" />
                          </Form.Item>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <div style={{ width: '110px', flexShrink: 0 }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'width']}
                            label="Width"
                            rules={[
                              { required: true, message: 'Width is required' },
                              { type: 'number', min: 10, message: 'Width must be at least 10' },
                            ]}
                            style={{ marginBottom: 8 }}
                          >
                            <InputNumber placeholder="1920" style={{ width: '100%' }} />
                          </Form.Item>
                        </div>
                        <div style={{ width: '110px', flexShrink: 0 }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'height']}
                            label="Height"
                            rules={[
                              { required: true, message: 'Height is required' },
                              { type: 'number', min: 10, message: 'Height must be at least 10' },
                            ]}
                            style={{ marginBottom: 8 }}
                          >
                            <InputNumber placeholder="1080" style={{ width: '100%' }} />
                          </Form.Item>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                        <div style={{ flex: 1 }}>
                          <Form.Item
                            {...restField}
                            name={[name, 'mediaCategoryId']}
                            label="Media Categories"
                            rules={[{ required: true, message: 'Media Categories are required' }]}
                            style={{ marginBottom: 8 }}
                          >
                            <Select
                              mode="multiple"
                              placeholder="Select Media Categories"
                              allowClear
                            >
                              {(() => {
                                const selectedCategories = form.getFieldValue('mediaCategoryId') || [];
                                return mediaCategoryList
                                  .filter(category => selectedCategories.includes(category._id))
                                  .map((category) => (
                                    <Option key={category._id} value={category._id}>
                                      {category.name}
                                    </Option>
                                  ));
                              })()}
                            </Select>
                          </Form.Item>
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <Button
                            type="link"
                            danger
                            onClick={() => remove(name)}
                            icon={<MinusCircleOutlined />}
                          >
                            Remove
                          </Button>
                        </div>
                      </div>
                    </Space>
                  ))}
                  <Form.Item>
                    <Button
                      type="dashed"
                      onClick={() => add()}
                      block
                      icon={<PlusOutlined />}
                    >
                      Add Screen Configuration
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>
          </>
        )}
      </Form>
    </Modal>
  );
}
